import type { ChangeEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { __ } from '@/hub-shared/utils/i18n';
import type { CabinetCompanySearchItem } from '../../api/integrationCompanies';
import { searchCabinetCompanies } from '../../api/integrationCompanies';
import { getProfileImportFieldLabel } from '../../companyProfileImportFieldCatalog';
import type { IntegrationApi } from '../../connectionTypes';
import type { FieldDiffItem, ImportRunItem, ImportRunState, MappingPreviewItem, IntegrationSelectionState, WizardMode } from '../types';
import { estimateImportDurationMinutes } from '../importWizardDuration';
import { mappingDisposition } from '../mappingReason';
import type { ImportJobState } from '../hooks/useImportJob';
import { extractErrorMessage } from '../../api/apiErrors';

type ImportConfirmModalProps = {
  open: boolean;
  onClose: () => void;
  mode: WizardMode;
  targetCompanyId: number | null;
  selectedConnections: IntegrationApi[];
  selectionsByConnectionId: Record<string, IntegrationSelectionState>;
  previewByConnectionId: Record<string, MappingPreviewItem[]>;
  loadingByConnectionId: Record<string, boolean>;
  errorByConnectionId: Record<string, string>;
  onRetryConnection: (connectionId: string) => Promise<void>;
  totalRemoteCompanies: number;
  remoteCompanyLabelsByConnectionId: Record<string, Record<string, string>>;
  attachTargetByRemoteId: Record<string, Record<string, number>>;
  allowCreateByRemoteId: Record<string, Record<string, boolean>>;
  onAttachChange: (connectionId: string, remoteCompanyId: string, companyId: number | null) => void;
  onAllowCreateChange: (connectionId: string, remoteCompanyId: string, allow: boolean) => void;
  api: { baseUrl: string; nonce: string };
  run: ImportRunState;
  runError: string;
  jobState?: ImportJobState;
  onConfirmImport: () => Promise<void>;
  onStopRun: () => void | Promise<void>;
};

type ConnectionCounts = {
  needsAction: number;
  ready: number;
  blocked: number;
};

type RunConnectionCounts = {
  pending: number;
  running: number;
  success: number;
  failed: number;
  stopped: number;
};

const ViewIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M9.57809 11.587C9.01452 11.7097 8.42463 11.7766 7.81928 11.7766C5.02814 11.7766 2.5644 10.3534 1.51343 8.60029C2.5644 6.84717 5.02814 5.42383 7.81928 5.42383C10.6104 5.42383 13.0742 6.84717 14.1251 8.60029C13.6544 9.38548 12.9001 10.1047 11.96 10.6593" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M7.81937 10.0162C8.60129 10.0162 9.23517 9.3823 9.23517 8.60037C9.23517 7.81845 8.60129 7.18457 7.81937 7.18457C7.03744 7.18457 6.40356 7.81845 6.40356 8.60037C6.40356 9.3823 7.03744 10.0162 7.81937 10.0162Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M7.81934 0.588379V3.76921" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M2.31372 1.64502L3.07722 2.96733" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M3.86035 4.32373L3.90438 4.39976" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M13.3249 1.64502L11.7344 4.39966" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);


const toStatusLabel = (status: ImportRunItem['status']): string => {
  if (status === 'pending') return __('En attente');
  if (status === 'running') return __('En cours');
  if (status === 'success') return __('Succès');
  if (status === 'failed') return __('Échec');
  return __('Arrêté');
};

const computeConnectionCounts = (
  items: MappingPreviewItem[],
  attachByRemoteId: Record<string, number>,
  allowCreateByRemoteId: Record<string, boolean>,
): ConnectionCounts => {
  let needsAction = 0;
  let ready = 0;
  let blocked = 0;
  for (const item of items) {
    const disposition = mappingDisposition(item.reason);
    if (disposition === 'blocked') {
      blocked += 1;
    } else if (disposition === 'needs_choice') {
      const hasAttach = (attachByRemoteId[item.remote_company_id] ?? 0) > 0;
      const allow = allowCreateByRemoteId[item.remote_company_id] ?? false;
      if (hasAttach || allow) {
        ready += 1;
      } else {
        needsAction += 1;
      }
    } else {
      ready += 1;
    }
  }
  return { needsAction, ready, blocked };
};

const blockedReasonLabel = (reason: string): string =>
  reason === 'siren_is_cabinet'
    ? __('SIREN de votre cabinet : il ne peut pas être importé comme dossier client.')
    : __('Correspondance non reconnue : l’entreprise ne sera pas importée.');

const computeRunConnectionCounts = (items: ImportRunItem[]): RunConnectionCounts => {
  const base: RunConnectionCounts = { pending: 0, running: 0, success: 0, failed: 0, stopped: 0 };
  return items.reduce(
    (acc, item) => ({ ...acc, [item.status]: acc[item.status] + 1 }),
    base,
  );
};

type ConfirmSidebarProps = {
  connections: IntegrationApi[];
  activeConnectionId: string;
  onSelect: (id: string) => void;
  previewByConnectionId: Record<string, MappingPreviewItem[]>;
  loadingByConnectionId: Record<string, boolean>;
  errorByConnectionId: Record<string, string>;
  attachTargetByRemoteId: Record<string, Record<string, number>>;
  allowCreateByRemoteId: Record<string, Record<string, boolean>>;
};

const ConfirmSidebar = ({
  connections,
  activeConnectionId,
  onSelect,
  previewByConnectionId,
  loadingByConnectionId,
  errorByConnectionId,
  attachTargetByRemoteId,
  allowCreateByRemoteId,
}: ConfirmSidebarProps) => (
  <ul className="import-confirm__sidebar-list" role="tablist" aria-label={__('Connecteurs')}>
    {connections.map((connection) => {
      const isActive = connection.id === activeConnectionId;
      const isLoading = loadingByConnectionId[connection.id] ?? false;
      const hasError = (errorByConnectionId[connection.id] ?? '') !== '';
      const counts = computeConnectionCounts(
        previewByConnectionId[connection.id] ?? [],
        attachTargetByRemoteId[connection.id] ?? {},
        allowCreateByRemoteId[connection.id] ?? {},
      );

      let statusBadge: string;
      if (isLoading) {
        statusBadge = '…';
      } else if (hasError) {
        statusBadge = '⚠';
      } else {
        statusBadge = `${counts.needsAction} / ${counts.ready}`;
      }

      return (
        <li key={connection.id} role="presentation">
          <button
            type="button"
            role="tab"
            aria-selected={isActive}
            className={`import-confirm__sidebar-btn${isActive ? ' import-confirm__sidebar-btn--active' : ''}`}
            onClick={() => onSelect(connection.id)}
          >
            <span className="import-confirm__sidebar-name">{connection.name}</span>
            <span className="import-confirm__sidebar-badge">{statusBadge}</span>
          </button>
        </li>
      );
    })}
  </ul>
);

type RunSidebarProps = {
  connections: IntegrationApi[];
  activeConnectionId: string;
  onSelect: (id: string) => void;
  runItems: ImportRunItem[];
};

const RunSidebar = ({
  connections,
  activeConnectionId,
  onSelect,
  runItems,
}: RunSidebarProps) => (
  <ul className="import-confirm__sidebar-list" role="tablist" aria-label={__('Connecteurs')}>
    {connections.map((connection) => {
      const isActive = connection.id === activeConnectionId;
      const connectionItems = runItems.filter((item) => item.connectionId === connection.id);
      const counts = computeRunConnectionCounts(connectionItems);
      const hasFailed = counts.failed > 0;

      return (
        <li key={connection.id} role="presentation">
          <button
            type="button"
            role="tab"
            aria-selected={isActive}
            className={`import-confirm__sidebar-btn${isActive ? ' import-confirm__sidebar-btn--active' : ''}${hasFailed ? ' import-confirm__sidebar-btn--has-errors' : ''}`}
            onClick={() => onSelect(connection.id)}
          >
            <span className="import-confirm__sidebar-name">{connection.name}</span>
            <span className="import-confirm__sidebar-badge">
              {counts.success > 0 ? `✓ ${counts.success}` : null}
              {counts.failed > 0 ? ` ✗ ${counts.failed}` : null}
              {counts.pending + counts.running > 0 ? ` · ${counts.pending + counts.running}` : null}
            </span>
          </button>
        </li>
      );
    })}
  </ul>
);

const ChevronIcon = ({ expanded }: { expanded: boolean }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
  >
    <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

type NeedsUserChoiceRowProps = {
  api: { baseUrl: string; nonce: string };
  connectionId: string;
  remoteCompanyId: string;
  label: string;
  attachCompanyId: number;
  allowCreate: boolean;
  onAttachChange: (connectionId: string, remoteCompanyId: string, companyId: number | null) => void;
  onAllowCreateChange: (connectionId: string, remoteCompanyId: string, allow: boolean) => void;
};

const NeedsUserChoiceRow = ({
  api,
  connectionId,
  remoteCompanyId,
  label,
  attachCompanyId,
  allowCreate,
  onAttachChange,
  onAllowCreateChange,
}: NeedsUserChoiceRowProps) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<CabinetCompanySearchItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [pickedLabel, setPickedLabel] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current !== null) {
      clearTimeout(debounceRef.current);
    }
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      setLoading(true);
      void searchCabinetCompanies(api, q)
        .then(setResults)
        .catch(() => setResults([]))
        .finally(() => {
          setLoading(false);
        });
    }, 300);
    return () => {
      if (debounceRef.current !== null) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [api, q]);

  const allowCreateId = `${connectionId}-${remoteCompanyId}-allow-create`;

  return (
    <li className="import-confirm__company-row import-confirm__company-row--choice">
      <div className="import-confirm__choice-head">
        <span className="import-confirm__company-name">{label}</span>
        <label className="import-confirm__allow-create" htmlFor={allowCreateId}>
          <input
            id={allowCreateId}
            type="checkbox"
            checked={allowCreate}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const next = event.target.checked;
              onAllowCreateChange(connectionId, remoteCompanyId, next);
              if (next) {
                onAttachChange(connectionId, remoteCompanyId, null);
                setPickedLabel('');
                setQ('');
              }
            }}
          />
          {__('Créer un nouveau dossier')}
        </label>
      </div>
      {allowCreate ? (
        <p className="import-confirm__detail-notice">{__('Un nouveau dossier sera créé à l’import.')}</p>
      ) : (
        <div className="import-confirm__attach-search">
          <input
            className="import-confirm__search-input"
            type="search"
            value={q}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setQ(event.target.value)}
            placeholder={__('Rechercher un dossier (nom ou SIREN)')}
            aria-label={__('Rechercher un dossier existant')}
          />
          {loading ? <span className="import-confirm__search-status">{__('Recherche…')}</span> : null}
          {results.length > 0 ? (
            <ul className="import-confirm__search-results" role="listbox">
              {results.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className="import-confirm__search-pick"
                    onClick={() => {
                      onAttachChange(connectionId, remoteCompanyId, c.id);
                      setPickedLabel(c.name);
                      setQ('');
                      setResults([]);
                    }}
                  >
                    {c.name}
                    {c.siren ? ` (${c.siren})` : ''}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {attachCompanyId > 0 && pickedLabel !== '' ? (
            <p className="import-confirm__picked">
              {__('Dossier :')} {pickedLabel} ({attachCompanyId})
            </p>
          ) : null}
        </div>
      )}
    </li>
  );
};

type FieldDiffTagsProps = {
  fieldDiff: FieldDiffItem[];
};

const FieldDiffTags = ({ fieldDiff }: FieldDiffTagsProps) => {
  const toAdd = fieldDiff.filter((f) => f.action === 'add');
  const toUpdate = fieldDiff.filter((f) => f.action === 'update');

  if (toAdd.length === 0 && toUpdate.length === 0) {
    return null;
  }

  return (
    <div className="import-confirm__field-diff">
      {toAdd.length > 0 ? (
        <div className="import-confirm__field-diff-group">
          <span className="import-confirm__field-diff-label import-confirm__field-diff-label--add">{__('Ajouté')}</span>
          <ul className="import-confirm__field-diff-tags">
            {toAdd.map((f) => (
              <li key={f.field_key} className="import-confirm__field-tag import-confirm__field-tag--add">
                {getProfileImportFieldLabel(f.field_key)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {toUpdate.length > 0 ? (
        <div className="import-confirm__field-diff-group">
          <span className="import-confirm__field-diff-label import-confirm__field-diff-label--update">{__('Mis à jour')}</span>
          <ul className="import-confirm__field-diff-tags">
            {toUpdate.map((f) => (
              <li key={f.field_key} className="import-confirm__field-tag import-confirm__field-tag--update">
                {getProfileImportFieldLabel(f.field_key)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

const PREVIEW_LIMIT = 5;

type ConfirmDetailProps = {
  connection: IntegrationApi;
  selectionsByConnectionId: Record<string, IntegrationSelectionState>;
  previewByConnectionId: Record<string, MappingPreviewItem[]>;
  loadingByConnectionId: Record<string, boolean>;
  errorByConnectionId: Record<string, string>;
  onRetryConnection: (connectionId: string) => Promise<void>;
  mode: WizardMode;
  remoteCompanyLabels: Record<string, string>;
  attachTargetByRemoteId: Record<string, Record<string, number>>;
  allowCreateByRemoteId: Record<string, Record<string, boolean>>;
  onAttachChange: (connectionId: string, remoteCompanyId: string, companyId: number | null) => void;
  onAllowCreateChange: (connectionId: string, remoteCompanyId: string, allow: boolean) => void;
  api: { baseUrl: string; nonce: string };
};

const ConfirmDetail = ({
  connection,
  selectionsByConnectionId,
  previewByConnectionId,
  loadingByConnectionId,
  errorByConnectionId,
  onRetryConnection,
  mode,
  remoteCompanyLabels,
  attachTargetByRemoteId,
  allowCreateByRemoteId,
  onAttachChange,
  onAllowCreateChange,
  api,
}: ConfirmDetailProps) => {
  const [showAll, setShowAll] = useState(false);
  const [expandedRemoteId, setExpandedRemoteId] = useState<string | null>(null);

  const isLoading = loadingByConnectionId[connection.id] ?? false;
  const error = errorByConnectionId[connection.id] ?? '';
  const previewItems = previewByConnectionId[connection.id] ?? [];
  const selectedCompanyIds = selectionsByConnectionId[connection.id]?.selectedRemoteCompanyIds ?? [];

  const getRemoteFallbackLabel = (remoteId: string) => remoteCompanyLabels[remoteId] ?? remoteId;
  const getPreviewRowLabel = (item: MappingPreviewItem) =>
    item.target_company_label ?? getRemoteFallbackLabel(item.remote_company_id);

  if (mode === 'single') {
    return (
      <div className="import-confirm__detail" role="tabpanel">
        <p className="import-confirm__detail-notice">
          {__('Les données sélectionnées seront importées dans le dossier choisi.')}
        </p>
        <ul className="import-confirm__company-list">
          {selectedCompanyIds.map((remoteCompanyId) => (
            <li key={remoteCompanyId} className="import-confirm__company-row">
              <span className="import-confirm__company-name">{getRemoteFallbackLabel(remoteCompanyId)}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="import-confirm__detail import-confirm__detail--loading" role="tabpanel">
        <p>{__('Prévisualisation du mapping…')}</p>
      </div>
    );
  }

  if (error !== '') {
    return (
      <div className="import-confirm__detail" role="tabpanel">
        <div role="alert" className="import-confirm__detail-error">
          <p>{error}</p>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => void onRetryConnection(connection.id)}
          >
            {__('Réessayer')}
          </button>
        </div>
      </div>
    );
  }

  const needsChoice = previewItems.filter((item) => mappingDisposition(item.reason) === 'needs_choice');
  const linked = previewItems.filter((item) => mappingDisposition(item.reason) === 'update');
  const strictMatch = previewItems.filter((item) => mappingDisposition(item.reason) === 'match');
  const createNew = previewItems.filter((item) => mappingDisposition(item.reason) === 'create');
  const promote = previewItems.filter((item) => mappingDisposition(item.reason) === 'promote');
  const blocked = previewItems.filter((item) => mappingDisposition(item.reason) === 'blocked');
  const visibleNeedsChoice = showAll ? needsChoice : needsChoice.slice(0, PREVIEW_LIMIT);
  const hiddenCount = Math.max(0, needsChoice.length - PREVIEW_LIMIT);
  const attachForConn = attachTargetByRemoteId[connection.id] ?? {};
  const allowForConn = allowCreateByRemoteId[connection.id] ?? {};

  return (
    <div className="import-confirm__detail" role="tabpanel">
      {needsChoice.length > 0 ? (
        <div className="import-confirm__detail-section">
          <div className="import-confirm__detail-section-header">
            <span className="import-confirm__detail-section-title">
              {__('Choisir le dossier cible')} ({needsChoice.length})
            </span>
          </div>
          <p className="import-confirm__detail-notice">
            {__('Sans correspondance automatique : associez un dossier existant ou cochez la création d’un nouveau dossier.')}
          </p>
          <ul className="import-confirm__company-list">
            {visibleNeedsChoice.map((item) => (
              <NeedsUserChoiceRow
                key={item.remote_company_id}
                api={api}
                connectionId={connection.id}
                remoteCompanyId={item.remote_company_id}
                label={getPreviewRowLabel(item)}
                attachCompanyId={attachForConn[item.remote_company_id] ?? 0}
                allowCreate={allowForConn[item.remote_company_id] ?? false}
                onAttachChange={onAttachChange}
                onAllowCreateChange={onAllowCreateChange}
              />
            ))}
            {!showAll && hiddenCount > 0 ? (
              <li className="import-confirm__company-row import-confirm__company-more">
                <button
                  type="button"
                  className="import-confirm__view-btn import-confirm__view-btn--inline"
                  onClick={() => setShowAll(true)}
                >
                  <ViewIcon />
                  {__('Voir les')} {hiddenCount} {__('autres')}
                </button>
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}

      {strictMatch.length > 0 ? (
        <div className="import-confirm__detail-section">
          <div className="import-confirm__detail-section-header">
            <span className="import-confirm__detail-section-title import-confirm__detail-section-title--match">
              {__('Dossier existant (correspondance stricte)')} ({strictMatch.length})
            </span>
          </div>
          <ul className="import-confirm__company-list">
            {strictMatch.map((item) => (
              <li key={item.remote_company_id} className="import-confirm__company-row import-confirm__company-row--match">
                <span className="import-confirm__company-name">{getPreviewRowLabel(item)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {createNew.length > 0 ? (
        <div className="import-confirm__detail-section">
          <div className="import-confirm__detail-section-header">
            <span className="import-confirm__detail-section-title import-confirm__detail-section-title--match">
              {__('Nouveau dossier (SIREN absent de vos dossiers)')} ({createNew.length})
            </span>
          </div>
          <p className="import-confirm__detail-notice">
            {__('Un dossier sera créé automatiquement à partir du SIREN fourni par le connecteur.')}
          </p>
          <ul className="import-confirm__company-list">
            {createNew.map((item) => (
              <li key={item.remote_company_id} className="import-confirm__company-row import-confirm__company-row--match">
                <span className="import-confirm__company-name">{getPreviewRowLabel(item)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {promote.length > 0 ? (
        <div className="import-confirm__detail-section">
          <div className="import-confirm__detail-section-header">
            <span className="import-confirm__detail-section-title">
              {__('Filiale déjà enregistrée : sera convertie en dossier')} ({promote.length})
            </span>
          </div>
          <p className="import-confirm__detail-notice">
            {__('Le SIREN appartient à une filiale déjà enregistrée dans votre cabinet : l’import la convertit en dossier client, sans créer de doublon.')}
          </p>
          <ul className="import-confirm__company-list">
            {promote.map((item) => (
              <li key={item.remote_company_id} className="import-confirm__company-row">
                <span className="import-confirm__company-name">{getPreviewRowLabel(item)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {blocked.length > 0 ? (
        <div className="import-confirm__detail-section">
          <div className="import-confirm__detail-section-header">
            <span className="import-confirm__detail-section-title import-confirm__detail-section-title--error">
              {__('Non importées')} ({blocked.length})
            </span>
          </div>
          <ul className="import-confirm__company-list">
            {blocked.map((item) => (
              <li key={item.remote_company_id} className="import-confirm__company-row import-confirm__company-row--failed">
                <span className="import-confirm__company-name">{getPreviewRowLabel(item)}</span>
                <span className="import-confirm__company-error">{blockedReasonLabel(item.reason)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {linked.length > 0 ? (
        <div className="import-confirm__detail-section">
          <div className="import-confirm__detail-section-header">
            <span className="import-confirm__detail-section-title">
              {__('Données mises à jour')} ({linked.length})
            </span>
          </div>
          <ul className="import-confirm__company-list">
            {linked.map((item) => {
              const hasDiff = Array.isArray(item.field_diff) && item.field_diff.length > 0;
              const isExpanded = expandedRemoteId === item.remote_company_id;
              return (
                <li key={item.remote_company_id} className="import-confirm__company-row import-confirm__company-row--linked">
                  {hasDiff ? (
                    <button
                      type="button"
                      className="import-confirm__company-expand-btn"
                      aria-expanded={isExpanded}
                      onClick={() => setExpandedRemoteId(isExpanded ? null : item.remote_company_id)}
                    >
                      <span className="import-confirm__company-name">{getPreviewRowLabel(item)}</span>
                      <ChevronIcon expanded={isExpanded} />
                    </button>
                  ) : (
                    <span className="import-confirm__company-name">{getPreviewRowLabel(item)}</span>
                  )}
                  {hasDiff && isExpanded ? (
                    <FieldDiffTags fieldDiff={item.field_diff!} />
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {previewItems.length === 0 ? (
        <p className="import-confirm__detail-notice">{__('Aucune entreprise sélectionnée.')}</p>
      ) : null}
    </div>
  );
};

type RunDetailProps = {
  connection: IntegrationApi;
  runItems: ImportRunItem[];
};

const RunDetail = ({ connection, runItems }: RunDetailProps) => {
  const connectionItems = runItems.filter((item) => item.connectionId === connection.id);
  const counts = computeRunConnectionCounts(connectionItems);
  const failedItems = connectionItems.filter((item) => item.status === 'failed');
  const total = connectionItems.length;
  const done = counts.success + counts.failed + counts.stopped;
  const progressPct = total > 0 ? Math.round((done / total) * 100) : 0;

  const allIgnoredKeys = useMemo(() => {
    const seen = new Set<string>();
    for (const item of connectionItems) {
      for (const key of item.ignoredFieldKeys ?? []) {
        seen.add(key);
      }
    }
    return Array.from(seen);
  }, [connectionItems]);

  return (
    <div className="import-confirm__detail" role="tabpanel">
      <div className="import-confirm__progress">
        <div className="import-confirm__progress-bar">
          <div
            className="import-confirm__progress-fill"
            style={{ width: `${progressPct}%` }}
            role="progressbar"
            aria-valuenow={progressPct}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
        <p className="import-confirm__progress-label">
          {done} / {total} — {toStatusLabel(counts.running > 0 ? 'running' : counts.pending > 0 ? 'pending' : 'success')}
        </p>
        <div className="import-confirm__run-counts">
          {counts.pending > 0 ? <span>{__('En attente')} {counts.pending}</span> : null}
          {counts.running > 0 ? <span>{__('En cours')} {counts.running}</span> : null}
          {counts.success > 0 ? <span className="import-confirm__run-count--success">✓ {counts.success}</span> : null}
          {counts.failed > 0 ? <span className="import-confirm__run-count--failed">✗ {counts.failed}</span> : null}
          {counts.stopped > 0 ? <span>{__('Arrêtés')} {counts.stopped}</span> : null}
        </div>
      </div>

      {allIgnoredKeys.length > 0 ? (
        <div className="import-confirm__detail-section import-confirm__detail-section--warning" role="note">
          <p className="import-confirm__warning-text">
            {__('Certains champs sélectionnés ne sont pas fournis par ce connecteur et ont été ignorés :')}
            {' '}
            {allIgnoredKeys.map((k) => getProfileImportFieldLabel(k)).join(', ')}
          </p>
        </div>
      ) : null}

      {failedItems.length > 0 ? (
        <div className="import-confirm__detail-section">
          <div className="import-confirm__detail-section-header">
            <span className="import-confirm__detail-section-title import-confirm__detail-section-title--error">
              {__('Échecs')} ({failedItems.length})
            </span>
          </div>
          <ul className="import-confirm__company-list">
            {failedItems.map((item) => (
              <li key={item.id} className="import-confirm__company-row import-confirm__company-row--failed">
                <span className="import-confirm__company-id">{item.remoteCompanyId}</span>
                {item.errorMessage ? (
                  <span className="import-confirm__company-error">{item.errorMessage}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

export const ImportConfirmModal = ({
  open,
  onClose,
  mode,
  targetCompanyId: _targetCompanyId,
  selectedConnections,
  selectionsByConnectionId,
  previewByConnectionId,
  loadingByConnectionId,
  errorByConnectionId,
  onRetryConnection,
  totalRemoteCompanies,
  remoteCompanyLabelsByConnectionId,
  attachTargetByRemoteId,
  allowCreateByRemoteId,
  onAttachChange,
  onAllowCreateChange,
  api,
  run,
  runError,
  jobState,
  onConfirmImport,
  onStopRun,
}: ImportConfirmModalProps) => {
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState('');
  const [activeConnectionId, setActiveConnectionId] = useState<string>(
    () => selectedConnections[0]?.id ?? '',
  );

  const runHint = useMemo(() => {
    if (jobState === undefined) {
      return '';
    }
    if (jobState.status === 'starting') {
      return __('Préparation de l’import…');
    }
    const jobStatus = jobState.job?.status ?? null;
    if (jobStatus === 'queued') {
      return __('Import en file d’attente…');
    }
    if (jobStatus === 'running') {
      return __('Import en cours…');
    }
    return '';
  }, [jobState]);

  const globalCounts = useMemo(() => {
    let needsAction = 0;
    let ready = 0;
    let blocked = 0;
    for (const connection of selectedConnections) {
      const counts = computeConnectionCounts(
        previewByConnectionId[connection.id] ?? [],
        attachTargetByRemoteId[connection.id] ?? {},
        allowCreateByRemoteId[connection.id] ?? {},
      );
      needsAction += counts.needsAction;
      ready += counts.ready;
      blocked += counts.blocked;
    }
    return { needsAction, ready, blocked };
  }, [selectedConnections, previewByConnectionId, attachTargetByRemoteId, allowCreateByRemoteId]);

  const hasUnresolvedNeedsChoice = useMemo(() => {
    if (mode !== 'bulk') {
      return false;
    }
    for (const connection of selectedConnections) {
      const items = previewByConnectionId[connection.id] ?? [];
      const attach = attachTargetByRemoteId[connection.id] ?? {};
      const allow = allowCreateByRemoteId[connection.id] ?? {};
      for (const item of items) {
        if (item.reason !== 'needs_user_choice') {
          continue;
        }
        const a = attach[item.remote_company_id] ?? 0;
        const ac = allow[item.remote_company_id] ?? false;
        if (!(a > 0 || ac)) {
          return true;
        }
      }
    }
    return false;
  }, [mode, selectedConnections, previewByConnectionId, attachTargetByRemoteId, allowCreateByRemoteId]);

  const globalRunCounts = useMemo(() => {
    if (jobState?.job !== null && jobState?.job !== undefined) {
      const { done_items, failed_items, total_items, status } = jobState.job;
      const remaining = Math.max(0, total_items - done_items - failed_items);
      const inFlight = status === 'running' || status === 'queued' ? remaining : 0;
      return {
        pending: inFlight,
        running: 0,
        success: done_items,
        failed: failed_items,
        stopped: 0,
      };
    }
    return computeRunConnectionCounts(run.items);
  }, [jobState, run.items]);

  const importHasStarted = jobState !== undefined
    ? jobState.status !== 'idle'
    : run.startedAtIso !== null;
  const canClose = !importHasStarted;
  const showConfirmFooter = !importHasStarted;
  const jobIsRunning = jobState !== undefined
    ? jobState.status === 'starting' || jobState.status === 'polling'
    : run.status === 'running';
  const showStop =
    importHasStarted &&
    jobIsRunning &&
    (jobState === undefined ? !run.stopRequested : true);

  const activeConnection = selectedConnections.find((c) => c.id === activeConnectionId) ?? selectedConnections[0];

  const handleOverlayClose = () => {
    if (canClose) {
      onClose();
    }
  };

  const handleConfirm = async () => {
    setActionError('');
    setSubmitting(true);
    try {
      await onConfirmImport();
    } catch (error) {
      setActionError(extractErrorMessage(error, __('Action impossible.')));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return null;
  }

  const isBulkConfirmDisabled =
    submitting ||
    (mode === 'bulk' &&
      (selectedConnections.some((c) => loadingByConnectionId[c.id] || (errorByConnectionId[c.id] ?? '') !== '') ||
        hasUnresolvedNeedsChoice));

  return (
    <div className="thn-modal-overlay" role="presentation" onClick={handleOverlayClose}>
      <div
        className="thn-modal thn-modal--large"
        role="dialog"
        aria-modal="true"
        aria-labelledby="import-confirm-title"
        onClick={(event) => event.stopPropagation()}
      >
        {canClose ? (
          <button type="button" className="thn-modal__close" onClick={onClose} aria-label={__('Fermer')}>
            <span aria-hidden>×</span>
          </button>
        ) : null}

        <div className="thn-modal__header">
          <h3 id="import-confirm-title">{__('Confirmer l\'import')}</h3>
          <div className="import-confirm__summary">
            {importHasStarted ? (
              <>
                <span className="import-confirm__summary-stat">
                  ✓ {globalRunCounts.success}
                </span>
                {globalRunCounts.failed > 0 ? (
                  <span className="import-confirm__summary-stat import-confirm__summary-stat--error">
                    ✗ {globalRunCounts.failed}
                  </span>
                ) : null}
                {globalRunCounts.pending + globalRunCounts.running > 0 ? (
                  <span className="import-confirm__summary-stat">
                    {__('En attente')} {globalRunCounts.pending + globalRunCounts.running}
                  </span>
                ) : null}
              </>
            ) : (
              <>
                {mode === 'bulk' ? (
                  <>
                    {globalCounts.needsAction > 0 ? (
                      <span className="import-confirm__summary-stat import-confirm__summary-stat--error">
                        {globalCounts.needsAction} {__('à confirmer')}
                      </span>
                    ) : null}
                    <span className="import-confirm__summary-stat import-confirm__summary-stat--muted">
                      {globalCounts.ready} {__('prêts')}
                    </span>
                    {globalCounts.blocked > 0 ? (
                      <span className="import-confirm__summary-stat import-confirm__summary-stat--error">
                        {`${globalCounts.blocked} ${__('non importées')}`}
                      </span>
                    ) : null}
                  </>
                ) : null}
                <span className="import-confirm__summary-stat import-confirm__summary-stat--muted">
                  ~{estimateImportDurationMinutes(totalRemoteCompanies)} {__('min')}
                </span>
              </>
            )}
          </div>
          {importHasStarted && runHint !== '' ? (
            <p className="import-confirm__placeholder">{runHint}</p>
          ) : null}
        </div>

        {(runError !== '' || actionError !== '') ? (
          <div className="import-confirm__global-error" role="alert">
            {runError || actionError}
          </div>
        ) : null}

        {selectedConnections.length > 0 && activeConnection ? (
          <div className="import-confirm__body">
            <nav className="import-confirm__sidebar">
              {importHasStarted ? (
                <RunSidebar
                  connections={selectedConnections}
                  activeConnectionId={activeConnection.id}
                  onSelect={setActiveConnectionId}
                  runItems={run.items}
                />
              ) : (
                <ConfirmSidebar
                  connections={selectedConnections}
                  activeConnectionId={activeConnection.id}
                  onSelect={setActiveConnectionId}
                  previewByConnectionId={previewByConnectionId}
                  loadingByConnectionId={loadingByConnectionId}
                  errorByConnectionId={errorByConnectionId}
                  attachTargetByRemoteId={attachTargetByRemoteId}
                  allowCreateByRemoteId={allowCreateByRemoteId}
                />
              )}
            </nav>
            <div className="import-confirm__detail-pane">
              {importHasStarted ? (
                run.items.length === 0 && runHint !== '' ? (
                  <div className="import-confirm__detail" role="tabpanel">
                    <div className="import-confirm__detail-section">
                      <p>{runHint}</p>
                      <p className="import-confirm__placeholder">{__('Ne fermez pas cette fenêtre.')}</p>
                    </div>
                  </div>
                ) : (
                  <RunDetail
                    connection={activeConnection}
                    runItems={run.items}
                  />
                )
              ) : (
                <ConfirmDetail
                  connection={activeConnection}
                  selectionsByConnectionId={selectionsByConnectionId}
                  previewByConnectionId={previewByConnectionId}
                  loadingByConnectionId={loadingByConnectionId}
                  errorByConnectionId={errorByConnectionId}
                  onRetryConnection={onRetryConnection}
                  mode={mode}
                  remoteCompanyLabels={remoteCompanyLabelsByConnectionId[activeConnection.id] ?? {}}
                  attachTargetByRemoteId={attachTargetByRemoteId}
                  allowCreateByRemoteId={allowCreateByRemoteId}
                  onAttachChange={onAttachChange}
                  onAllowCreateChange={onAllowCreateChange}
                  api={api}
                />
              )}
            </div>
          </div>
        ) : null}

        <div className="thn-modal__footer">
          {showConfirmFooter ? (
            <>
              <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>
                {__('Annuler')}
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => void handleConfirm()}
                disabled={isBulkConfirmDisabled}
              >
                {submitting ? __('Import…') : __('Confirmer l\'import')}
              </button>
            </>
          ) : null}
          {showStop ? (
            <button
              type="button"
              className="btn-secondary"
              onClick={onStopRun}
              disabled={run.stopRequested}
            >
              {run.stopRequested ? __('Arrêt demandé…') : __('Stop')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};
